import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { ApiService } from '../../authapi.service';
import Logger from '../../logger.service';
import { showToast } from '../toast/toast.actions';
import { logIn, logInFailed, logInSuccess, resendToken, resendTokenFailed, resendTokenSuccess, restoreLastSession, restoreLastSessionFailed, restoreLastSessionSuccess, signOut, signOutSuccess } from './auth.actions';
import { NavController } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import { ErrorResponse, User } from 'src/app/synaps-media.types';

const log = Logger('effects:auth');

export const LS_TOKEN_KEY = 'auth';
export const LS_TOKEN_EXPIRE_KEY = 'auth_expire';

@Injectable()
export class AuthEffects {
  restoreLastSession$ = createEffect(() => this.actions$.pipe(
    ofType(restoreLastSession),
    switchMap(() => {
      log('restoreLastSession');

      return this.api.me().pipe(
        tap((user) => log('restored user', user)),
        map((user) => restoreLastSessionSuccess({ user })),
        tap(() => log('session set')),
        catchError((error) => {
          console.error(error);
          let redirectUrl = location.href.replace(location.origin, '');
          if (redirectUrl.startsWith('/login')) {
            redirectUrl = '/'
          }
          return of(restoreLastSessionFailed({ redirectUrl }));
        })
      );

    }),
  ));

  logIn$ = createEffect(() => this.actions$.pipe(
    ofType(logIn),
    mergeMap(({ username, password, redirectUrl }) => this.api.login(username, password)
      .pipe(
        map(({data: { user }}) => logInSuccess({ user, redirectUrl })),
        catchError((error: ErrorResponse) => {
          log('login failed', error);
          return of(logInFailed(error.errors[0]));
        })
      )
    ),
  ));

  loginSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(logInSuccess),
    tap(({ redirectUrl }) => {
      location.href = redirectUrl || '/';
    }),
  ), { dispatch: false });

  resendToken$ = createEffect(() => this.actions$.pipe(
    ofType(resendToken),
    switchMap(() => this.api.resendToken()),
    tap(() => {
      log('resend token');
    }),
    map(() => resendTokenSuccess()),
    catchError((error: ErrorResponse) => {
      log('resend token failed', error);
      return of(resendTokenFailed(error.errors[0]));
    })
  ));

  signOut$ = createEffect(() => this.actions$.pipe(
    ofType(signOut),
    switchMap(() => this.api.signOut()),
    tap(() => {
      log('sign out');
      window.$chatwoot.reset();
      // this.router.navigateByUrl('/login')
      this.navCtrl.navigateRoot('/login');
    }),
    map(() => signOutSuccess()),
  ));

  signOutSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(signOutSuccess),
    map(() => {
      log('sign out success');
      return showToast({ message: 'You signed out successfully!', color: 'success' });
    })
  ));

  waitForChatwoot$ = createEffect(() => this.actions$.pipe(
    ofType(logInSuccess, restoreLastSessionSuccess),
    switchMap(({ user }) => {
      return new Observable((observer) => {
        const interval = setInterval(() => {
          if (window.$chatwoot) {
            clearInterval(interval);
            log('chatwoot ready');
            observer.next({ user });
            observer.complete();
          }
        }, 1000);
      });
    }),
    take<{ user: User }>(1),
    tap(({ user }) => {

      console.log(user);

      window.$chatwoot.setUser(`${user.cw_identifier}`, {
        identifier_hash: user.cw_hmac_token,
        email: user.email,
        name: user.email,
        company_name: user.sites?.map(site => site.serviceName).join(', '),
      });

      window.$chatwoot.setCustomAttributes({
        // userId: user.id,
        // memberSince: user.createdAt,
        siteCount: user.sites?.length || 0,
      });
    })
  ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private navCtrl: NavController
  ) { }

  ngrxOnInitEffects() {

    return restoreLastSession();
  }

}
